import { createContext, useContext, useMemo, useReducer, type ReactNode } from 'react';

import {
  computeHasChanges,
  editProjectReducer,
  initialEditProjectState,
  type EditProjectState,
  type ReallocationOperation,
} from './editProjectReducer';
import type { User } from '../types/response';

interface EditProjectContextValue extends EditProjectState {
  projectId: string;
  hasChanges: boolean;
  enterEditMode: () => void;
  exitEditMode: () => void;
  cancelEdits: () => void;
  addAnnotator: (person: User) => void;
  removeAnnotator: (annotatorId: string) => void;
  addSupervisor: (person: User) => void;
  removeSupervisor: (supervisorId: string) => void;
  commitReallocations: (operations: ReallocationOperation[]) => void;
  clearReallocations: () => void;
}

const noop = () => undefined;

/**
 * Default value so components (e.g. ProjectOverview) can render outside of an
 * EditProjectProvider in a read-only state — mirrors SupervisorModeContext.
 */
const defaultValue: EditProjectContextValue = {
  ...initialEditProjectState,
  projectId: '',
  hasChanges: false,
  enterEditMode: noop,
  exitEditMode: noop,
  cancelEdits: noop,
  addAnnotator: noop,
  removeAnnotator: noop,
  addSupervisor: noop,
  removeSupervisor: noop,
  commitReallocations: noop,
  clearReallocations: noop,
};

const EditProjectContext = createContext<EditProjectContextValue>(defaultValue);

export const useEditProject = (): EditProjectContextValue => useContext(EditProjectContext);

interface EditProjectProviderProps {
  projectId: string;
  /** Start in edit mode (e.g. when navigated to with ?edit=1). */
  initialEditMode?: boolean;
  children: ReactNode;
}

export const EditProjectProvider = ({
  projectId,
  initialEditMode = false,
  children,
}: EditProjectProviderProps) => {
  const [state, dispatch] = useReducer(editProjectReducer, {
    ...initialEditProjectState,
    editMode: initialEditMode,
  });

  const value = useMemo<EditProjectContextValue>(
    () => ({
      ...state,
      projectId,
      hasChanges: computeHasChanges(state),
      enterEditMode: () => dispatch({ type: 'ENTER_EDIT_MODE' }),
      exitEditMode: () => dispatch({ type: 'EXIT_EDIT_MODE' }),
      cancelEdits: () => dispatch({ type: 'CANCEL_EDITS' }),
      addAnnotator: (person) => dispatch({ type: 'ADD_ANNOTATOR', person }),
      removeAnnotator: (annotatorId) => dispatch({ type: 'REMOVE_ANNOTATOR', annotatorId }),
      addSupervisor: (person) => dispatch({ type: 'ADD_SUPERVISOR', person }),
      removeSupervisor: (supervisorId) => dispatch({ type: 'REMOVE_SUPERVISOR', supervisorId }),
      commitReallocations: (operations) => dispatch({ type: 'COMMIT_REALLOCATIONS', operations }),
      clearReallocations: () => dispatch({ type: 'CLEAR_REALLOCATIONS' }),
    }),
    [state, projectId]
  );

  return <EditProjectContext.Provider value={value}>{children}</EditProjectContext.Provider>;
};
